/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/CountryCodeSelector/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useMemo } from "react";
import Select, { components } from "react-select";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import styles from "./CountryCodeSelector.module.css";
import { getCountryCodeOptions } from "../../data/selectOptions";

const DropdownIndicator = (props) => {
  const { selectProps } = props;

  return (
    <components.DropdownIndicator {...props}>
      <KeyboardArrowDown
        style={{
          transform: selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease-in-out",
        }}
      />
    </components.DropdownIndicator>
  );
};

const CountryCodeSelector = ({
  value,
  onChange,
  isDisabled,
  placeholder,
  containerWidth = "115%",
}) => {
  const { t, i18n } = useTranslation();
  const countryCodeOptions = useMemo(
    () => getCountryCodeOptions(t),
    [t, i18n.language],
  );

  return (
    <Select
      className="select__control"
      components={{ DropdownIndicator }}
      placeholder={
        placeholder ?? t("components.contactInformation.countryCodeSelectorPlaceholder")
      }
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          width: containerWidth,
          height: "40px",
          fontSize: "16px",
          cursor: isDisabled ? "not-allowed" : "pointer",
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
          borderTopRightRadius: "0px",
          borderBottomRightRadius: "0px",
          opacity: isDisabled ? 0.6 : 1,
        }),
        container: (baseStyles) => ({
          ...baseStyles,
          width: "120px",
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
        }),
        input: (baseStyles) => ({
          ...baseStyles,
        }),
        option: (baseStyles) => ({
          ...baseStyles,
          cursor: "pointer",
        }),
        indicatorSeparator: () => ({ display: "none" }),
        menu: (baseStyles) => ({
          ...baseStyles,
          zIndex: 9999,
          position: "absolute",
          width: "110px",
        }),
        menuPortal: (baseStyles) => ({
          ...baseStyles,
          zIndex: 9999,
        }),
      }}
      // menuPortalTarget={document.body}
      isSearchable={false}
      value={value}
      onChange={onChange}
      options={countryCodeOptions}
      isDisabled={isDisabled}
      formatOptionLabel={(option) => (
        <div className={styles.country}>
          <img
            className={styles.country_img}
            src={option.image}
            alt={t("common.countryFlagAlt")}
          />
          <span className={styles.country_code}>{option.code}</span>
        </div>
      )}
    />
  );
};

export default CountryCodeSelector;
